import { useCallback, useEffect, useState } from "react";
import {
  feedbackApi,
  type FeedbackItem,
  type FeedbackStatus,
} from "../../../api";
import { getApiErrorMessage } from "../../../utils";

export type FeedbackFilter = FeedbackStatus | "all";

type UseFeedbackInboxOptions = {
  onOpenCountChange?: (count: number) => void;
};

export function useFeedbackInbox({
  onOpenCountChange,
}: UseFeedbackInboxOptions) {
  const [filter, setFilter] = useState<FeedbackFilter>("open");
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await feedbackApi.listAdmin(
        filter === "all" ? undefined : filter,
      );
      const nextItems = response.data.items;
      setItems(nextItems);
      onOpenCountChange?.(response.data.open_count);

      setSelectedId((currentSelectedId) => {
        if (
          currentSelectedId &&
          nextItems.some((item) => item.id === currentSelectedId)
        ) {
          return currentSelectedId;
        }
        return nextItems[0]?.id ?? null;
      });
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, "Failed to load feedback"));
    } finally {
      setLoading(false);
    }
  }, [filter, onOpenCountChange]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadFeedback();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadFeedback]);

  useEffect(() => {
    let isActive = true;
    const timeoutId = window.setTimeout(() => {
      if (!selectedId) {
        setSelected(null);
        setResolutionNote("");
        return;
      }

      setDetailLoading(true);
      feedbackApi
        .getAdmin(selectedId)
        .then((response) => {
          if (!isActive) return;
          setSelected(response.data);
          setResolutionNote(response.data.resolution_note ?? "");
        })
        .catch(() => {
          if (!isActive) return;
          setSelected(null);
          setResolutionNote("");
        })
        .finally(() => {
          if (isActive) setDetailLoading(false);
        });
    }, 0);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [selectedId]);

  const handleStatusChange = useCallback(
    async (status: FeedbackStatus) => {
      if (!selected) return;

      setSaving(true);
      setError(null);
      try {
        const response = await feedbackApi.updateAdmin(selected.id, {
          status,
          resolution_note: resolutionNote.trim() || null,
        });
        setSelected(response.data);
        setResolutionNote(response.data.resolution_note ?? "");
        await loadFeedback();
      } catch (saveError) {
        setError(getApiErrorMessage(saveError, "Failed to update feedback"));
      } finally {
        setSaving(false);
      }
    },
    [loadFeedback, resolutionNote, selected],
  );

  return {
    filter,
    setFilter,
    items,
    selectedId,
    setSelectedId,
    selected,
    resolutionNote,
    setResolutionNote,
    loading,
    detailLoading,
    saving,
    error,
    loadFeedback,
    handleStatusChange,
  };
}
